// PropertyImages.jsx - Single scrollable page with sticky footer
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Button,
  Divider,
  Tooltip,
  Checkbox,
  CircularProgress,
  Autocomplete,
  Chip,
  Paper,
  TextField,
  Collapse,
  Fade,
  Zoom,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  AppBar,
  Toolbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import HotelIcon from '@mui/icons-material/Hotel';
import PhotoCameraFrontIcon from '@mui/icons-material/PhotoCameraFront';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BathtubIcon from '@mui/icons-material/Bathtub';
import KingBedIcon from '@mui/icons-material/KingBed';
import BedIcon from '@mui/icons-material/Bed';
import BedroomParentIcon from '@mui/icons-material/BedroomParent';
import BedroomChildIcon from '@mui/icons-material/BedroomChild';
import CollectionsIcon from '@mui/icons-material/Collections';
import WifiIcon from '@mui/icons-material/Wifi';
import PoolIcon from '@mui/icons-material/Pool';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import useHotelList from '../hooks/useHotelList';
import useCustomToast from '../hooks/useCustomToast';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { dedupeRequest } from '../utils/requestDedupe';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import amenitiesSections from '../components/amenitiesData';
import AmenitiesModal from '../components/AmenitiesModal';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

function decryptData(ciphertext) {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

function parseHotelImages(hotelImagesStr) {
  try {
    let parsed = JSON.parse(hotelImagesStr);
    
    // If it's already an object with the right structure, return as is
    if (typeof parsed === 'object' && parsed !== null) {
      // Normalize the data structure
      const normalized = {};
      
      Object.keys(parsed).forEach(key => {
        const value = parsed[key];
        
        if (Array.isArray(value)) {
          // If it's an array, ensure each item has id, url, and label
          normalized[key] = value.map((item, index) => {
            if (typeof item === 'string') {
              // If item is just a string (URL), convert to object
              return {
                id: `${key}_${index}`,
                url: item,
                label: key === 'main_image' ? 'Main Image' : `${key.replace(/_/g, ' ')} ${index + 1}`
              };
            } else if (typeof item === 'object' && item !== null) {
              // If item is an object, ensure it has required properties
              const urlValue = item.url || item.path || item.image || item;
              return {
                id: item.id || `${key}_${index}`,
                url: typeof urlValue === 'string' ? urlValue : String(urlValue || ''),
                label: item.label || item.name || (key === 'main_image' ? 'Main Image' : `${key.replace(/_/g, ' ')} ${index + 1}`)
              };
            }
            return item;
          });
        } else if (typeof value === 'string') {
          // If it's a single string, convert to array with one item
          normalized[key] = [{
            id: `${key}_1`,
            url: value,
            label: key === 'main_image' ? 'Main Image' : key.replace(/_/g, ' ')
          }];
        } else if (value && typeof value === 'object') {
          // If it's a single object, convert to array with one item
          const urlValue = value.url || value.path || value.image || value;
          normalized[key] = [{
            id: value.id || `${key}_1`,
            url: typeof urlValue === 'string' ? urlValue : String(urlValue || ''),
            label: value.label || value.name || (key === 'main_image' ? 'Main Image' : key.replace(/_/g, ' '))
          }];
        } else {
          normalized[key] = [];
        }
      });
      
      return normalized;
    }
    
    return {};
  } catch (error) {
    console.error('Error parsing hotel images:', error);
    return {};
  }
}

function getImageUrl(path) {
  // Ensure path is a string
  if (!path || typeof path !== 'string') return '';
  
  // If it's already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // If it's a blob URL, return as is
  if (path.startsWith('blob:')) {
    return path;
  }
  
  // If it's a relative path, construct the full API URL
  if (path.startsWith('/')) {
    return `https://api.tabtrips.com/olivrweb/hotelapi${path}`;
  }
  
  // If it's a relative path without leading slash, add it
  return `https://api.tabtrips.com/olivrweb/hotelapi/${path}`;
}

/** Explicitly include JPG/JPEG plus common image types (do not narrow image/*). */
const PROPERTY_IMAGE_ACCEPT =
  'image/*,.jpg,.jpeg,.jpe,.jfif,image/jpeg,image/jpg,image/pjpeg,.png,image/png,.webp,image/webp,.gif,image/gif';

const ALLOWED_IMAGE_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'jpe', 'jfif', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif', 'avif',
]);

function isAllowedImageFile(file) {
  if (!file) return false;
  if (file.type && String(file.type).startsWith('image/')) return true;
  // Some browsers/OS leave MIME empty for .jpg — fall back to extension.
  const name = String(file.name || '');
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  return ALLOWED_IMAGE_EXTENSIONS.has(ext);
}

const PropertyImages = () => {
  const { hotels, loading: hotelsLoading } = useHotelList();
  const { successToast, errorToast } = useCustomToast();
  const fileInputRef = useRef(null);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [hotelName, setHotelName] = useState('');
  const [imagesByType, setImagesByType] = useState({});
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState({ open: false, url: '', label: '', isLocal: false });
  const [selectedImages, setSelectedImages] = useState([]);
  /** Selection for bulk delete only; main_image is never selectable */
  const deletableSelectedImages = useMemo(
    () => selectedImages.filter((id) => !id.startsWith('main_image::')),
    [selectedImages]
  );
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadingImages, setLoadingImages] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', severity: 'info' });

  // Add state for improved image upload flow
  const [pendingImages, setPendingImages] = useState({}); // Store temporary images before upload
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingSection, setUploadingSection] = useState('');
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  // File input refs for each section
  const fileInputRefs = useRef({});

  // Room types state
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypesLoading, setRoomTypesLoading] = useState(false);

  // Amenities state
  const [amenitiesData, setAmenitiesData] = useState([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(false);
  const [amenitiesDropdownOpen, setAmenitiesDropdownOpen] = useState({});
  const [editingAmenities, setEditingAmenities] = useState({});
  const [savingAmenities, setSavingAmenities] = useState({});
  const [amenitiesLoadingTimeout, setAmenitiesLoadingTimeout] = useState(null);

  // Add state for amenities modal
  const [amenitiesModalOpen, setAmenitiesModalOpen] = useState(false);
  const [modalSelected, setModalSelected] = useState({});
  const [modalCategoryId, setModalCategoryId] = useState(null);

  // Amenities list for multi-select
  const amenitiesList = [
    "Free Wi-Fi", "TV", "Air Conditioning", "Mini Bar", "Safe", "Desk", "Bathtub", "Shower", "Balcony", "Sea View",
    "Pool", "Gym", "Restaurant", "Spa", "Parking", "Room Service", "Laundry", "Business Center", "Conference Room"
  ];

  // Dynamic sections based on room types from API
  const [allSections, setAllSections] = useState([]);

  const THUMBNAIL_SIZE = { xs: 140, sm: 180, md: 200 };

  // Fetch room types from API
  const fetchRoomTypes = async (hotelId) => {
    if (!hotelId) return;
    
    setRoomTypesLoading(true);
    try {
      const token = getDecryptedToken();
      if (!token) {
        errorToast("Please log in to view room types");
        return;
      }

      const response = await dedupeRequest(
        `getAllRoomTypes:${hotelId}`,
        () =>
          axios.post(
            'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getAllRoomTypes',
            { token, hotel_id: parseInt(hotelId, 10) },
            { headers: { 'Content-Type': 'application/json' } }
          ),
        3000
      );

      if (response.data.status && Array.isArray(response.data.rooms_details)) {
        setRoomTypes(response.data.rooms_details);
        
        // Generate dynamic sections based on room types
        const roomTypeSections = response.data.rooms_details.map(room => {
          const roomName = room.safe_name || room.category_name || 'Unknown Room';
          const formattedName = roomName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          return {
            key: roomName,
            label: `${formattedName} Images`,
            icon: <BedIcon fontSize="medium" />
          };
        });
        
        // Update allSections with dynamic room type sections
        // First, get the base sections (non-room sections)
        const baseSections = [
          { key: 'main_image', label: 'Main Image', icon: <HotelIcon fontSize="medium" /> },
          { key: 'hotel_images', label: 'Property Images', icon: <HotelIcon fontSize="medium" /> },
        ];
        
        // Combine base sections with room type sections
        setAllSections([...baseSections, ...roomTypeSections]);
      } else {
        setRoomTypes([]);
        errorToast(response.data.message || "Failed to fetch room types");
      }
    } catch (error) {
      console.error("Error fetching room types:", error);
      errorToast("An error occurred while fetching room types");
      setRoomTypes([]);
    } finally {
      setRoomTypesLoading(false);
    }
  };

  // Helper to fetch latest hotel images from API
  const fetchHotelImagesFromApi = async (hotelId) => {
    try {
      setLoadingImages(true);
      const token = getDecryptedToken();
      if (!token) return {};
      const formData = new URLSearchParams({ hotel_id: hotelId, token });
      const response = await dedupeRequest(
        `getHotelDetails:${hotelId}:images`,
        () => axios.post('https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelDetails', formData),
        3000
      );
      if (response.data && response.data.hotel_details && response.data.hotel_details.hotel_images) {
        return parseHotelImages(response.data.hotel_details.hotel_images);
      }
    } catch (e) {
      console.error('API Error:', e);
      // fallback to localStorage if API fails
    }
    
    // fallback to localStorage
    const hotelsListStr = localStorage.getItem('hotelsList');
    if (hotelsListStr && hotelId) {
      try {
        const hotels = decryptData(hotelsListStr);
        const hotel = hotels.find(h => String(h.hotel_id) === String(hotelId));
        if (hotel && hotel.hotel_images) {
          return parseHotelImages(hotel.hotel_images);
        }
      } catch {}
    }
    setLoadingImages(false);
    return {};
  };

  // Function to refresh images from API and update state
  const refreshImages = async () => {
    const id = localStorage.getItem('selectedHotelId');
    const name = localStorage.getItem('selectedHotelName');
    setSelectedHotel(id);
    setHotelName(name || 'Selected property');
    if (id) {
      const hotelImages = await fetchHotelImagesFromApi(id);
      setImagesByType(hotelImages);
    } else {
      setImagesByType({});
    }
  };

  useEffect(() => {
    refreshImages();
    const id = localStorage.getItem('selectedHotelId');
    if (id) fetchRoomTypes(id);

    const handleStorageChange = (e) => {
      if (e.key === 'selectedHotelId' || e.key === 'selectedHotelName') {
        refreshImages();
        const nextId = localStorage.getItem('selectedHotelId');
        if (nextId) fetchRoomTypes(nextId);
      }
    };
    const handleHotelSelected = () => {
      refreshImages();
      const nextId = localStorage.getItem('selectedHotelId');
      if (nextId) fetchRoomTypes(nextId);
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('hotelSelected', handleHotelSelected);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('hotelSelected', handleHotelSelected);
    };
  }, []);

  // Main image cannot be multi-selected or deleted — only replaced via upload
  useEffect(() => {
    setSelectedImages((prev) => prev.filter((id) => !id.startsWith('main_image::')));
  }, []);

  // Handle image selection
  const handleImageSelect = (imageId, sectionKey) => {
    if (sectionKey === 'main_image') return;
    const imageKey = `${sectionKey}::${imageId}`;
    setSelectedImages(prev => 
      prev.includes(imageKey) 
        ? prev.filter(id => id !== imageKey)
        : [...prev, imageKey]
    );
  };

  // Handle select all images in a section
  const handleSelectAllInSection = (sectionKey) => {
    if (sectionKey === 'main_image') return;
    const sectionImages = imagesByType[sectionKey] || [];
    const sectionImageKeys = sectionImages.map(img => `${sectionKey}::${img.id}`);
    
    // Check if all images in section are already selected
    const allSelected = sectionImageKeys.every(key => selectedImages.includes(key));
    
    if (allSelected) {
      // Deselect all images in this section
      setSelectedImages(prev => prev.filter(key => !sectionImageKeys.includes(key)));
    } else {
      // Select all images in this section
      setSelectedImages(prev => [...new Set([...prev, ...sectionImageKeys])]);
    }
  };

  // Handle select all images
  const handleSelectAll = () => {
    const allImageKeys = [];
    Object.keys(imagesByType).forEach(sectionKey => {
      if (sectionKey === 'main_image') return;
      const sectionImages = imagesByType[sectionKey] || [];
      sectionImages.forEach(img => {
        allImageKeys.push(`${sectionKey}::${img.id}`);
      });
    });
    
    // Check if all images are already selected
    const allSelected = allImageKeys.every(key => selectedImages.includes(key));
    
    if (allSelected) {
      // Deselect all images
      setSelectedImages([]);
    } else {
      // Select all images
      setSelectedImages(allImageKeys);
    }
  };

  // Handle delete selected images
  const handleDeleteSelected = async () => {
    const keysToDelete = deletableSelectedImages;
    if (keysToDelete.length === 0) {
      errorToast('Please select images to delete');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = getDecryptedToken();
      if (!token) {
        errorToast('Authentication token is missing. Please log in again.');
        return;
      }

      // Get all image paths from selected images (never main_image)
      const imagePaths = [];
      keysToDelete.forEach(imageKey => {
        const separatorIndex = imageKey.indexOf('::');
        if (separatorIndex === -1) return;
        
        const sectionKey = imageKey.substring(0, separatorIndex);
        if (sectionKey === 'main_image') return;
        const imageId = imageKey.substring(separatorIndex + 2);
        
        const image = imagesByType[sectionKey]?.find(img => img.id.toString() === imageId);
        if (image && image.url) {
          imagePaths.push(image.url);
        }
      });

      if (imagePaths.length === 0) {
        errorToast('No valid images found to delete');
        return;
      }

      const payload = {
        hotel_id: parseInt(selectedHotel),
        token: token,
        image_paths: imagePaths
      };

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/deleteHotelImages',
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data && response.data.status) {
        successToast(`Successfully deleted ${imagePaths.length} image${imagePaths.length > 1 ? 's' : ''}`);
        
        // Remove deleted images from local state
        keysToDelete.forEach(imageKey => {
          const separatorIndex = imageKey.indexOf('::');
          if (separatorIndex === -1) return;
          
          const sectionKey = imageKey.substring(0, separatorIndex);
          const imageId = imageKey.substring(separatorIndex + 2);
          
          setImagesByType(prev => ({
            ...prev,
            [sectionKey]: (prev[sectionKey] || []).filter(img => img.id.toString() !== imageId)
          }));
        });
        
        setSelectedImages([]);
      } else {
        errorToast('Failed to delete images. Please try again.');
      }

    } catch (error) {
      console.error('Delete images error:', error);
      errorToast('Failed to delete images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle add image to section
  const handleAddImage = (sectionKey) => {
    // Create file input if it doesn't exist
    if (!fileInputRefs.current[sectionKey]) {
      fileInputRefs.current[sectionKey] = document.createElement('input');
      fileInputRefs.current[sectionKey].type = 'file';
      fileInputRefs.current[sectionKey].multiple = sectionKey !== 'main_image'; // Only one image for main
      fileInputRefs.current[sectionKey].onchange = (e) => handleFileChange(e, sectionKey);
    }
    // Always keep accept list current (includes JPG/JPEG explicitly)
    fileInputRefs.current[sectionKey].accept = PROPERTY_IMAGE_ACCEPT;
    
    // Trigger file input
    fileInputRefs.current[sectionKey].click();
  };

  // Handle file selection and auto-upload
  const handleFileChange = async (event, sectionKey) => {
    const files = Array.from(event.target.files || []).filter(isAllowedImageFile);
    if (!files.length) {
      errorToast('Please select image files (JPG, JPEG, PNG, WebP, GIF, etc.)');
      event.target.value = '';
      return;
    }

    // Limit to one file for main_image
    const selectedFiles = sectionKey === 'main_image' ? files.slice(0, 1) : files;

    // Clear the file input immediately
    event.target.value = '';

    // Start upload immediately
    await uploadImagesToSection(selectedFiles, sectionKey);
  };

  // Upload images to specific section
  const uploadImagesToSection = async (files, sectionKey) => {
    if (!selectedHotel || !files.length) {
      errorToast('No files to upload');
      return;
    }

    setUploadingSection(sectionKey);
    setUploadingImages(true);

    try {
      const token = getDecryptedToken();
      if (!token) {
        errorToast('Please log in to upload images');
        return;
      }

      // Create FormData for upload
      const formData = new FormData();
      formData.append('hotel_id', selectedHotel);
      formData.append('token', token);

      // Add files to formData using the correct format (sectionName[])
      files.forEach(file => {
        formData.append(`${sectionKey}[]`, file);
      });

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/updateImage',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      if (response.data && response.data.status) {
        successToast(`Successfully uploaded ${files.length} image${files.length > 1 ? 's' : ''} to ${getSectionLabel(sectionKey)}`);
        
        // Update the imagesByType with the new images
        if (response.data.added_images && response.data.added_images[sectionKey]) {
          const imagePaths = response.data.added_images[sectionKey];
          if (Array.isArray(imagePaths)) {
            const newImages = imagePaths.map((imagePath, index) => ({
              id: `uploaded_${Date.now()}_${index}`,
              url: imagePath,
              label: files[index]?.name?.replace(/\.[^/.]+$/, '') || `Uploaded Image ${index + 1}`,
              isLocal: false
            }));
            
            setImagesByType(prev => ({
              ...prev,
              [sectionKey]: [
                ...(prev[sectionKey] || []),
                ...newImages
              ]
            }));
          }
        }
        
        setUploadProgress(0);
      } else {
        errorToast(response.data?.message || 'Failed to upload images. Please try again.');
      }

    } catch (error) {
      console.error('Upload images error:', error);
      if (error.response?.data?.message) {
        errorToast(`Upload Error: ${error.response.data.message}`);
      } else {
        errorToast('Failed to upload images. Please try again.');
      }
    } finally {
      setUploadingImages(false);
      setUploadProgress(0);
      setUploadingSection('');
    }
  };

  // Handle drag and drop
  const handleDragOver = (e, sectionKey) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = 'rgba(255, 119, 0, 0.1)';
  };

  const handleDragLeave = (e, sectionKey) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = 'transparent';
  };

  const handleDrop = async (e, sectionKey) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = 'transparent';
    
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;

    // Filter image files (MIME or extension — JPG often has empty MIME on some devices)
    const imageFiles = files.filter(isAllowedImageFile);
    if (!imageFiles.length) {
      errorToast('Please drop image files (JPG, JPEG, PNG, WebP, GIF, etc.)');
      return;
    }

    // Limit to one file for main_image
    const selectedFiles = sectionKey === 'main_image' ? imageFiles.slice(0, 1) : imageFiles;

    // Start upload immediately
    await uploadImagesToSection(selectedFiles, sectionKey);
  };

  // Remove pending image
  const removePendingImage = (sectionKey, imageId) => {
    setPendingImages(prev => {
      const updated = { ...prev };
      if (updated[sectionKey]) {
        updated[sectionKey] = updated[sectionKey].filter(img => img.id !== imageId);
        if (updated[sectionKey].length === 0) {
          delete updated[sectionKey];
        }
      }
      setHasUnsavedChanges(Object.keys(updated).length > 0);
      return updated;
    });
  };

  // Save all pending images
  const saveAllImages = async () => {
    if (!selectedHotel || Object.keys(pendingImages).length === 0) {
      errorToast('No images to save');
      return;
    }

    setUploadingImages(true);
    let totalSuccess = 0;
    let totalErrors = 0;

    try {
      const token = getDecryptedToken();
      if (!token) {
        errorToast('Please log in to save images');
        return;
      }

      // Group images by section for batch upload
      const formData = new FormData();
      formData.append('hotel_id', selectedHotel);
      formData.append('token', token);

      // Add images for each section using the correct format (sectionName[])
      Object.entries(pendingImages).forEach(([sectionKey, images]) => {
        images.forEach(image => {
          formData.append(`${sectionKey}[]`, image.file);
        });
      });

      const payload = {
        hotel_id: selectedHotel,
        token: token,
        sections: Object.keys(pendingImages)
      };

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/updateImage',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      if (response.data && response.data.status) {
        totalSuccess = Object.values(pendingImages).flat().length;
        successToast(`Successfully uploaded ${totalSuccess} image${totalSuccess > 1 ? 's' : ''}`);
        
        // Update the imagesByType with the new images
        if (response.data.added_images) {
          Object.entries(response.data.added_images).forEach(([sectionKey, imagePaths]) => {
            if (Array.isArray(imagePaths)) {
              const newImages = imagePaths.map((imagePath, index) => ({
                id: `uploaded_${Date.now()}_${index}`,
                url: imagePath,
                label: `Uploaded Image ${index + 1}`,
                isLocal: false
              }));
              
              setImagesByType(prev => ({
                ...prev,
                [sectionKey]: [
                  ...(prev[sectionKey] || []),
                  ...newImages
                ]
              }));
            }
          });
        }
        
        // Clear pending images
        setPendingImages({});
        setHasUnsavedChanges(false);
        setUploadProgress(0);
        setUploadingSection('');
      } else {
        errorToast(response.data.message || 'Failed to upload images. Please try again.');
      }

    } catch (error) {
      console.error('Upload images error:', error);
      if (error.response?.data?.message) {
        errorToast(`API Error: ${error.response.data.message}`);
      } else {
        errorToast('Failed to upload images. Please try again.');
      }
    } finally {
      setUploadingImages(false);
      setUploadProgress(0);
      setUploadingSection('');
    }
  };

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(pendingImages).flat().forEach(img => {
        if (img.url && img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });
    };
  }, [pendingImages]);

  // Handle cancel selection
  const handleCancelSelection = () => {
    setSelectedImages([]);
  };

  // Get section icon
  const getSectionIcon = (sectionKey) => {
    const section = allSections.find(s => s.key === sectionKey);
    return section ? section.icon : <CollectionsIcon />;
  };

  // Get section label
  const getSectionLabel = (sectionKey) => {
    const section = allSections.find(s => s.key === sectionKey);
    return section ? section.label : 'Unknown Section';
  };

  // Render image card
  const renderImageCard = (image, sectionKey, index) => {
    const imageKey = `${sectionKey}::${image.id}`;
    const isSelectable = sectionKey !== 'main_image';
    const isSelected = isSelectable && selectedImages.includes(imageKey);
    
    // Skip rendering if image doesn't have a valid URL
    if (!image.url || typeof image.url !== 'string') {
      return null;
    }
    
    return (
      <Grid item xs={6} sm={6} md={4} lg={3} key={imageKey}>
        <Card sx={{
          position: 'relative',
          boxShadow: 2,
          cursor: 'pointer',
          borderRadius: 2,
          width: '100%',
          height: { xs: 200, sm: 240, md: 280, lg: 320 }, // Fixed card height
          backgroundColor: '#ffffff',
          border: isSelected ? '3px solid #FF7700' : '2px solid #ffffff',
          transition: 'all 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          }
        }}>
          {/* Selection checkbox — hidden for main image (replace only via upload) */}
          {isSelectable && (
            <Box sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              zIndex: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '50%',
              padding: '1px'
            }}>
              <Checkbox
                checked={isSelected}
                onChange={() => handleImageSelect(image.id, sectionKey)}
                size="small"
                sx={{
                  color: '#FF7700',
                  '&.Mui-checked': {
                    color: '#FF7700',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1rem'
                  }
                }}
              />
            </Box>
          )}

          {/* Image Container with Fixed Aspect Ratio */}
          <Box sx={{
            position: 'relative',
            width: '100%',
            height: { xs: 155, sm: 190, md: 225, lg: 260 }, // Adjusted to balance with visible footer
            overflow: 'hidden',
            borderRadius: '8px 8px 0 0',
          flex: '1 1 auto'
          }}>
            <CardMedia
              component="img"
              image={getImageUrl(image.url)}
              alt={image.label || 'Property image'}
              sx={{
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                objectFit: 'cover', // This ensures the image covers the container while maintaining aspect ratio
                objectPosition: 'center' // Center the image within the container
              }}
              onClick={() => setPreview({ 
                open: true, 
                url: getImageUrl(image.url), 
                label: image.label || 'Property image',
                isLocal: false
              })}
            />
          </Box>

          {/* Image Info with Proper Height for Text Visibility */}
          <CardContent sx={{ 
            p: { xs: 0.75, sm: 1, md: 1.25 }, // Increased padding for better visibility
            height: { xs: 45, sm: 50, md: 55, lg: 60 }, // Increased height to show text properly
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: '0 0 auto',
            backgroundColor: 'rgba(255, 255, 255, 0.95)' // Slight background for better contrast
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' }, // Increased font size
                fontWeight: 500,
                textAlign: 'center',
                color: '#374151', // Darker color for better visibility
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3
              }}
            >
              {image.label || 'Property image'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // Render section
  const renderSection = (section) => {
    const sectionImages = imagesByType[section.key] || [];
    const sectionImageKeys = sectionImages.map(img => `${section.key}::${img.id}`);
    const isMainSection = section.key === 'main_image';
    const allSectionSelected =
      !isMainSection &&
      sectionImages.length > 0 &&
      sectionImageKeys.every(key => selectedImages.includes(key));
    const isUploading = uploadingSection === section.key;
    
    return (
      <Box key={section.key} sx={{ mb: 4 }}>
        {/* Section Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: { xs: 2, sm: 3 },
          p: { xs: 1.5, sm: 2 },
          backgroundColor: '#ffffff',
          borderRadius: 2,
          border: '1px solid rgba(255, 119, 0, 0.1)',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              backgroundColor: '#FF7700',
              borderRadius: '50%',
              color: '#ffffff'
            }}>
              {section.icon}
            </Box>
            <Box>
              <Typography 
                variant="h6" 
                fontWeight={700} 
                sx={{ 
                  color: '#1e293b',
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                }}
              >
                {section.label}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {sectionImages.length} image{sectionImages.length !== 1 ? 's' : ''}
              </Typography>
              {isMainSection && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#94a3b8',
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    display: 'block',
                    mt: 0.5,
                    maxWidth: 320,
                  }}
                >
                  Upload a new photo to replace the current one. The main image cannot be selected or deleted here.
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMainSection && (
              <Button
                variant="outlined"
                onClick={() => handleSelectAllInSection(section.key)}
                disabled={sectionImages.length === 0}
                size="small"
                sx={{
                  borderColor: allSectionSelected ? '#FF7700' : 'rgba(0, 0, 0, 0.23)',
                  color: allSectionSelected ? '#FF7700' : '#000000',
                  backgroundColor: '#ffffff',
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.75 },
                  '&:hover': {
                    borderColor: '#FF7700',
                    backgroundColor: 'rgba(255, 119, 0, 0.04)'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.38)',
                    backgroundColor: '#ffffff'
                  }
                }}
              >
                {allSectionSelected ? 'Deselect All' : 'Select All'}
              </Button>
            )}
            
            <Button
              variant="contained"
              startIcon={<AddPhotoAlternateIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />}
              onClick={() => handleAddImage(section.key)}
              disabled={isUploading}
              size="small"
              sx={{
                background: 'linear-gradient(90deg, #FF7700 0%, #FFA040 100%)',
                color: '#ffffff',
                fontWeight: 600,
                borderRadius: 2,
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.75 },
                '&:hover': {
                  background: 'linear-gradient(90deg, #FF7700 0%, #FF7700 100%)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.38)'
                }
              }}
            >
              {isUploading
                ? 'Uploading...'
                : isMainSection && sectionImages.length > 0
                  ? 'Replace image'
                  : 'Add Photo'}
            </Button>
          </Box>
        </Box>

        {/* Section Images */}
        <Box
          onDragOver={(e) => handleDragOver(e, section.key)}
          onDragLeave={(e) => handleDragLeave(e, section.key)}
          onDrop={(e) => handleDrop(e, section.key)}
          sx={{
            minHeight: 200,
            border: '2px dashed transparent',
            borderRadius: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(255, 119, 0, 0.3)',
              backgroundColor: 'rgba(255, 119, 0, 0.02)'
            }
          }}
        >
          <Grid container spacing={2}>
            {/* Render existing images */}
            {sectionImages
              .filter(image => image && image.url && typeof image.url === 'string')
              .map((image, index) => renderImageCard(image, section.key, index))}
            
            {/* Always show Add Image card */}
            <Grid item xs={6} sm={6} md={4} lg={3}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: { xs: 200, sm: 240, md: 280, lg: 320 }, // Same height as image cards
                backgroundColor: isUploading ? 'rgba(255, 119, 0, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                borderRadius: 2,
                border: isUploading ? '2px dashed rgba(255, 119, 0, 0.5)' : '2px dashed rgba(0, 0, 0, 0.1)',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': isUploading ? {} : {
                  borderColor: 'rgba(255, 119, 0, 0.5)',
                  backgroundColor: 'rgba(255, 119, 0, 0.05)'
                }
              }}
              onClick={() => !isUploading && handleAddImage(section.key)}
              >
                {isUploading ? (
                  <CircularProgress size={32} sx={{ color: '#FF7700', mb: { xs: 1, sm: 1.5 } }} />
                ) : (
                  <AddPhotoAlternateIcon sx={{ 
                    fontSize: { xs: 24, sm: 32, md: 40 }, 
                    color: 'text.secondary', 
                    mb: { xs: 1, sm: 1.5 } 
                  }} />
                )}
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textAlign: 'center',
                  px: 1
                }}
              >
                {isUploading
                  ? 'Uploading...'
                  : isMainSection && sectionImages.length > 0
                    ? 'Replace main image'
                    : sectionImages.length === 0
                      ? 'No images yet'
                      : 'Add more images'}
              </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    mb: { xs: 1, sm: 1.5 }, 
                    textAlign: 'center',
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    px: 1
                  }}
                >
                  {isUploading
                    ? `${uploadProgress}% complete`
                    : isMainSection && sectionImages.length > 0
                      ? 'Upload replaces the current photo (cannot delete separately)'
                      : sectionImages.length === 0
                        ? 'Click to add first image'
                        : 'Click to add more'}
                </Typography>
                {!isUploading && (
                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    size="small"
                    sx={{
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                      px: { xs: 0.5, sm: 1 },
                      py: { xs: 0.25, sm: 0.5 },
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      '&:hover': {
                        borderColor: '#FF7700',
                        backgroundColor: 'rgba(255, 119, 0, 0.04)'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddImage(section.key);
                    }}
                  >
                    {isMainSection && sectionImages.length > 0 ? 'Replace' : 'Add Image'}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Upload Progress */}
        {uploadingSection === section.key && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 119, 0, 0.1)', borderRadius: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ 
                height: { xs: 6, sm: 8 },
                borderRadius: 1,
                backgroundColor: 'rgba(255, 119, 0, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#FF7700'
                }
              }} 
            />
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                mt: 1, 
                display: 'block',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            >
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ 
      maxWidth: 1200, 
      mx: 'auto', 
      pb: 10, // Add bottom padding for sticky footer
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: '#ffffff', 
        borderRadius: 3, 
        p: { xs: 2, sm: 3 }, 
        mb: { xs: 2, sm: 3 },
        boxShadow: 2,
        border: '1px solid rgba(0, 0, 0, 0.1)'
      }}>
        {/* Property info */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              backgroundColor: '#FF7700',
              borderRadius: '50%',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(255, 119, 0, 0.3)'
            }}>
              <HotelIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Box>
            <Box>
              <Typography 
                variant="h4" 
                fontWeight={700} 
                sx={{ 
                  color: '#1e293b',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                }}
              >
                Property Images
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748b',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {hotelName}
              </Typography>
            </Box>
          </Box>

          {/* Global Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* <Button
              variant="outlined"
              onClick={handleSelectAll}
              size="small"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.75 }
              }}
            >
              {selectedImages.length > 0 ? 'Deselect All' : 'Select All'}
            </Button> */}
            
            {deletableSelectedImages.length > 0 && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {deletableSelectedImages.length} image{deletableSelectedImages.length !== 1 ? 's' : ''} selected
              </Typography>
            )}

            {/* Upload Status */}
            {uploadingImages && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                backgroundColor: 'rgba(255, 119, 0, 0.1)',
                borderRadius: 1,
                border: '1px solid rgba(255, 119, 0, 0.3)'
              }}>
                <CircularProgress size={16} sx={{ color: '#FF7700' }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#FF7700',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  Uploading images...
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} sx={{ color: '#FF7700' }} />
          </Box>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {allSections.map(section => renderSection(section))}
          </motion.div>
        )}
      </Box>

      {/* Sticky Footer - Only when deletable images are selected (not main image) */}
      {deletableSelectedImages.length > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ffffff',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          py: { xs: 1, sm: 1.5 },
          px: { xs: 2, sm: 3 }
        }}>
          <Box sx={{
            maxWidth: 1200,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}>
            {/* Left side - Selection info */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: 1
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 24, sm: 28 },
                height: { xs: 24, sm: 28 },
                backgroundColor: '#FF7700',
                borderRadius: '50%',
                color: '#ffffff'
              }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontWeight: 600
                  }}
                >
                  {deletableSelectedImages.length}
                </Typography>
              </Box>
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    fontWeight: 600,
                    color: '#1e293b',
                    lineHeight: 1.2
                  }}
                >
                  {deletableSelectedImages.length} image{deletableSelectedImages.length !== 1 ? 's' : ''} selected
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    color: '#64748b',
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  Ready for deletion
                </Typography>
              </Box>
            </Box>

            {/* Right side - Action buttons */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 }
            }}>
              <Button
                variant="outlined"
                onClick={handleCancelSelection}
                size="small"
                sx={{
                  borderColor: 'rgba(0, 0, 0, 0.2)',
                  color: '#64748b',
                  backgroundColor: '#ffffff',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 0.75 },
                  minWidth: { xs: 'auto', sm: 80 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  '&:hover': {
                    borderColor: '#64748b',
                    backgroundColor: 'rgba(100, 116, 139, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                onClick={handleDeleteSelected}
                disabled={loading}
                size="small"
                sx={{
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  fontWeight: 600,
                  px: { xs: 1.5, sm: 2.5 },
                  py: { xs: 0.75, sm: 1 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                  '&:hover': {
                    backgroundColor: '#b91c1c',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.38)',
                    boxShadow: 'none'
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Deleting...</span>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Delete</span>
                    <Box sx={{
                      display: { xs: 'none', sm: 'inline' }
                    }}>
                      ({deletableSelectedImages.length})
                    </Box>
                  </Box>
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={preview.open}
        onClose={() => setPreview({ ...preview, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>
          {preview.label}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <img
              src={preview.url}
              alt={preview.label}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 8
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview({ ...preview, open: false })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyImages; 